import React, { lazy, Suspense } from "react";
import "./CardGames.css";
import { Seo } from "../Shared/Seo/Seo.tsx";
import { IntroLead, IntroRest } from "../Landing/IntroParagraphs.tsx";
import { findBySlug } from "../Shared/Seo/routeSeo.ts";
import { useCanPreviewPdf } from "../Shared/Pdf/useCanPreviewPdf.ts";
import { useTranslation } from "../Shared/i18n/useTranslation.ts";
import { format } from "../Shared/i18n/format.ts";
import type { StringKey } from "../Shared/i18n/strings.ts";
import { cardGame } from "./games.ts";
import type { CardGameId } from "./games.ts";
import { cardGameFileName, cardGamePageCount, quickFacts, ruleSections } from "./ruleSections.ts";
import type { RuleSectionKey } from "./ruleSections.ts";
import { CardGamePreviewPlaceholder } from "./CardGamePreviewPlaceholder.tsx";

// The only route into @react-pdf/renderer from these pages; see the preview.
const CardGamePreview = lazy(() => import("./CardGamePreview.tsx"));

/** Each rule's heading in the catalogue, written out so the key scan in StringKeys.test.js sees every one. */
const HEADING_KEYS: Record<RuleSectionKey, { labelKey: StringKey }> = {
    goal: { labelKey: "cardGames.goal" },
    youNeed: { labelKey: "cardGames.youNeed" },
    setUp: { labelKey: "cardGames.setUp" },
    howToPlay: { labelKey: "cardGames.howToPlay" },
    example: { labelKey: "cardGames.example" },
    winning: { labelKey: "cardGames.winning" },
    easier: { labelKey: "cardGames.easier" },
    harder: { labelKey: "cardGames.harder" },
    practises: { labelKey: "cardGames.practises" },
};

interface CardGamePageProps {
    slug: string;
    id: CardGameId;
}

/**
 * One printable card game (ADR 0135): a keyword landing page around the print section and the
 * game's rules. There are no settings - a deck is fixed - so the page is the h1 and intro, the
 * quick facts, the PDF (rules, cards and any record sheets) to print in a click, then the same
 * rules the PDF's first page carries, from the same strings (games.ts), then the FAQ.
 *
 * The print section comes before the rules on purpose: an adult who has the game in mind should
 * be able to print it without scrolling past the explanation.
 */
export function CardGamePage({ slug, id }: CardGamePageProps) {
    const entry = findBySlug(slug)!;
    const game = cardGame(id);
    const t = useTranslation();
    const canPreview = useCanPreviewPdf();

    const sheets = Math.ceil(game.deck.length / 8);
    const printed = {
        pages: cardGamePageCount(game),
        cards: game.deck.length,
        sheets,
        records: game.recordSheet?.copies ?? 0,
    };
    const summary = game.recordSheet
        ? format(t("cardGames.printSummaryTemplate"), printed)
        : format(t("cardGames.printSummaryNoRecordsTemplate"), printed);
    const headings = Object.fromEntries(
        Object.entries(HEADING_KEYS).map(([key, { labelKey }]) => [key, t(labelKey)])
    ) as Record<RuleSectionKey, string>;

    return (
        <>
            <Seo entry={entry} />
            <div className="landingContent">
                <h1>{entry.h1}</h1>
                <IntroLead paragraphs={entry.intro} />
            </div>

            <div className="cardGameTool">
                <p className="cardGameFacts">{quickFacts(game)}</p>

                <section className="cardGamePrint" aria-labelledby="cardGamePrintHeading">
                    <h2 id="cardGamePrintHeading">{t("cardGames.printTheGame")}</h2>
                    <p className="cardGamePrintSummary">{summary}</p>
                    <p className="cardGamePrintTip">{t("cardGames.printTip")}</p>
                    <Suspense
                        fallback={
                            <CardGamePreviewPlaceholder
                                canPreview={canPreview}
                                buildingLabel={t("cardGames.buildingPdf")}
                            />
                        }
                    >
                        <CardGamePreview
                            id={id}
                            canPreview={canPreview}
                            fileName={cardGameFileName(game)}
                            eventParams={{ game: game.name, pages: printed.pages }}
                        />
                    </Suspense>
                </section>

                <section className="cardGameRules" aria-labelledby="cardGameRulesHeading">
                    <h2 id="cardGameRulesHeading">{format(t("cardGames.rulesHeadingTemplate"), { name: game.name })}</h2>
                    {ruleSections(game, headings).map((section) => (
                        <div key={section.key} className="cardGameRule">
                            <h3>{section.heading}</h3>
                            {section.kind === "paragraph" && <p>{section.items[0]}</p>}
                            {section.kind === "bullets" && (
                                <ul>
                                    {section.items.map((item, i) => (
                                        <li key={i}>{item}</li>
                                    ))}
                                </ul>
                            )}
                            {section.kind === "numbered" && (
                                <ol>
                                    {section.items.map((item, i) => (
                                        <li key={i}>{item}</li>
                                    ))}
                                </ol>
                            )}
                        </div>
                    ))}
                </section>
            </div>

            <IntroRest paragraphs={entry.intro} />

            {entry.faq && entry.faq.length > 0 && (
                <section className="landingFaq">
                    <h2>{t("flashCards.frequentlyAskedQuestions")}</h2>
                    {entry.faq.map((item, i) => (
                        <div key={i} className="faqItem">
                            <h3>{item.q}</h3>
                            <p>{item.a}</p>
                        </div>
                    ))}
                </section>
            )}
        </>
    );
}

export default CardGamePage;
